import { countForPar, effectiveHolePars, isMultiStartRound, holeInPlayingRange } from "../golf";
import type { Round } from "../types";

/** Returns Set<playerId> of players whose current-hole score is COUNTING
 *  toward the team total on the specified 0-indexed hole.
 *  Strict top-N: exactly N players, ties broken by sort order (matching teamRoundPoints). */
export function countingPlayersForHole(
  round: Round,
  teamPlayerIds: string[],
  hole0: number,
  parAtHole: number,
): Set<string> {
  const hs = round.hole_scores ?? {};
  const hole_nr = round.hole_nr ?? {};
  const entries = teamPlayerIds
    .map((pid) => {
      const isNr = hole_nr[pid]?.includes(hole0) ?? false;
      const rawPts = hs[pid]?.[hole0];
      // NR = 0 pts. Marked separately so real scores always beat NR.
      return { pid, pts: isNr ? 0 : rawPts, isNr };
    })
    .filter((e) => Number.isFinite(e.pts) && (e.pts as number) >= 0) as { pid: string; pts: number; isNr: boolean }[];
  if (entries.length === 0) return new Set();
  const countN = countForPar(round, parAtHole, teamPlayerIds.length);
  // Sort: highest pts first, NR always last among equal pts values
  entries.sort((a, b) => {
    if (b.pts !== a.pts) return b.pts - a.pts;
    // Same pts: real score beats NR
    if (a.isNr !== b.isNr) return a.isNr ? 1 : -1;
    return 0;
  });
  const take = Math.min(countN, entries.length);
  return new Set(entries.slice(0, take).map((e) => e.pid));
}

export function playerCountingContribution(
  round: Round,
  teamPlayerIds: string[],
  playerId: string,
  throughHole?: number,
  groupIndex?: number,
): number {
  const pars = effectiveHolePars(round);
  if (pars.length !== 18) return 0;
  const cap = Math.min(17, throughHole ?? 17);
  const throughPos = cap + 1; // 1-indexed playing position
  const multiStart = isMultiStartRound(round) && groupIndex != null && groupIndex >= 0;
  const hs = round.hole_scores ?? {};
  const hole_nr = round.hole_nr;
  let contrib = 0;

  for (let h = 0; h < 18; h++) {
    // Apply the same hole-range filter as teamRoundPointsThrough
    if (multiStart) {
      if (!holeInPlayingRange(round, groupIndex!, h, throughPos)) continue;
    } else {
      if (h > cap) continue;
    }
    const par = pars[h] ?? 4;
    const countN = countForPar(round, par, teamPlayerIds.length);
    const entries = teamPlayerIds
      .map((pid) => {
        const isNr = hole_nr?.[pid]?.includes(h) ?? false;
        return { pid, pts: isNr ? 0 : hs[pid]?.[h], isNr };
      })
      .filter((e): e is { pid: string; pts: number; isNr: boolean } => Number.isFinite(e.pts) && (e.pts as number) >= 0);
    if (entries.length === 0) continue;

    // Sort: highest pts first, NR last among equal pts values
    entries.sort((a, b) => {
      if (b.pts !== a.pts) return b.pts - a.pts;
      if (a.isNr !== b.isNr) return a.isNr ? 1 : -1;
      return 0;
    });
    const take = Math.min(countN, entries.length);
    for (let k = 0; k < take; k++) {
      if (entries[k].pid === playerId) {
        contrib += entries[k].pts;
        break;
      }
    }
  }
  return contrib;
}
